/**
 * Phase WARDROBE-STYLE-YOURSELF-1 Commit 2
 *
 * Clueless-style combinator. Additive in this commit — NOT imported anywhere.
 * Commit 3 wires it into Wardrobe.tsx and removes PolaroidRail.
 *
 * Locked decisions (see brief):
 *   D2 — 3 primary slots (Top/Bottom/Shoes) + 2 optional slots
 *        (Outerwear/Accessory) shown as "+ Add" when empty.
 *   D4 — Dress auto-detected: when Top slot's item.category === "dress",
 *        Bottom slot is greyed with "Covered by dress" copy. No toggle pill.
 *   Q1 — Component-local readiness derivation
 *        (meta.lookbook_status === "ready"). currentOutfitId is cleared
 *        on back/try-another/tweak to stop polling. We do NOT use
 *        useOutfitMetaPolling's shared isTerminal predicate because it
 *        requires analysis_status === "complete" AND LB1/LB2 columns —
 *        style_yourself rows only ever run LB4.
 *   Q2 — analysis_status: "complete" written on INSERT (see comment at
 *        the insert site below).
 *   Q3 — userTouchedRef flips on scroll, surprise-me, and the (Commit 4)
 *        empty-slot tap. Pending style-swipe-batch resolution never
 *        clobbers active user interaction.
 *   Q17 — Loading copy: "Putting it together on you" / "About 60 seconds".
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWardrobeCombinations } from "@/hooks/useWardrobeCombinations";
import { useOutfitMetaPolling } from "@/hooks/useOutfitMetaPolling";
import { WardrobeImg } from "@/components/WardrobeImg";
import { Button } from "@/components/ui/button";
import { StyleYourselfResult } from "./StyleYourselfResult";
import { StyleYourselfHeader } from "./StyleYourselfHeader";
import type { SwipeCombination } from "@/lib/swipeSession";

export type StyleYourselfWardrobeItem = {
  id: string;
  name: string;
  category: string;
  color_hex: string | null;
  image_url: string | null;
  image_path: string | null;
  cutout_path: string | null;
  cutout_status: string | null;
  // LB4 contract fields — required for D3/D4/D6 to fire correctly
  pattern?: string | null;
  style_tags?: string[] | null;
  vision_description?: string | null;
  lb3_description?: string | null;
  lb4_description?: string | null;
  lb4_eligible?: boolean | null;
  lb3_eligible?: boolean | null;
  last_used_at?: string | null;
  times_used?: number | null;
  created_at?: string | null;
};

type SlotKey = "top" | "bottom" | "shoes" | "outerwear" | "accessory";

type Props = {
  userId: string;
  wardrobeItems: StyleYourselfWardrobeItem[];
  occasion: string;
  weather: string;
  mood: string;
  gender: string | null;
  modelSetup: Record<string, unknown> | null;
  modelProfile: import("@/components/LookbookLoadingSilhouette").LookbookModelProfile;
  onClose: () => void;
  onOpenDrawer: () => void;
};


// ---------------------------------------------------------------------------
// Category routing
// ---------------------------------------------------------------------------

// Top slot accepts tops AND dresses — scrolling Top to a dress is the
// signal that triggers Bottom auto-grey (D4).
function categoryMatchesSlot(category: string, slot: SlotKey): boolean {
  const c = (category || "").toLowerCase();
  switch (slot) {
    case "top":
      return c === "top" || c === "dress";
    case "bottom":
      return c === "bottom";
    case "shoes":
      return c === "shoes";
    case "outerwear":
      return c === "outerwear";
    case "accessory":
      return c === "accessory" || c === "bag";
  }
}

function itemsForSlot(items: StyleYourselfWardrobeItem[], slot: SlotKey) {
  return items.filter((i) => categoryMatchesSlot(i.category, slot));
}

// ---------------------------------------------------------------------------
// Pre-population
// ---------------------------------------------------------------------------

function mapComboToSlots(
  combo: SwipeCombination,
  items: StyleYourselfWardrobeItem[],
): Record<SlotKey, string | null> {
  const next: Record<SlotKey, string | null> = {
    top: null,
    bottom: null,
    shoes: null,
    outerwear: null,
    accessory: null,
  };
  const byId = new Map(items.map((i) => [i.id, i]));
  for (const id of combo.item_ids) {
    const it = byId.get(id);
    if (!it) continue;
    const c = (it.category || "").toLowerCase();
    if ((c === "top" || c === "dress") && !next.top) next.top = id;
    else if (c === "bottom" && !next.bottom) next.bottom = id;
    else if (c === "shoes" && !next.shoes) next.shoes = id;
    else if (c === "outerwear" && !next.outerwear) next.outerwear = id;
    else if ((c === "accessory" || c === "bag") && !next.accessory)
      next.accessory = id;
  }
  return next;
}

// Recency fallback: most-recently-added item per primary slot. Outerwear
// and accessory left null (optional). Used when style-swipe-batch returns
// empty (new users without taste profile).
function pickRecencyFallback(
  items: StyleYourselfWardrobeItem[],
): Record<SlotKey, string | null> {
  const byRecency = [...items].sort((a, b) => {
    const ta = a.created_at ? Date.parse(a.created_at) : 0;
    const tb = b.created_at ? Date.parse(b.created_at) : 0;
    return tb - ta;
  });
  const pickFirst = (slot: SlotKey) =>
    byRecency.find((i) => categoryMatchesSlot(i.category, slot))?.id ?? null;
  return {
    top: pickFirst("top"),
    bottom: pickFirst("bottom"),
    shoes: pickFirst("shoes"),
    outerwear: null,
    accessory: null,
  };
}

// ---------------------------------------------------------------------------
// SlotRow — horizontal scroll cycler with surprise-me + (Commit 4) empty tap
// ---------------------------------------------------------------------------

type SlotRowProps = {
  slot: SlotKey;
  label: string;
  itemId: string | null;
  options: StyleYourselfWardrobeItem[];
  optional: boolean;
  disabled?: boolean;
  disabledCopy?: string;
  onChange: (id: string | null) => void;
  onUserTouch: () => void;
  // Commit 4 — Capacitor Camera picker will call this from the empty tile tap.
  // Exposed here so Commit 4 only needs to plug a handler in, not refactor state.
  onRequestUpload?: () => void;
};

function SlotRow({
  slot,
  label,
  itemId,
  options,
  optional,
  disabled,
  disabledCopy,
  onChange,
  onUserTouch,
  onRequestUpload,
}: SlotRowProps) {
  const current = itemId ? options.find((o) => o.id === itemId) ?? null : null;
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const cycle = (dir: 1 | -1) => {
    if (disabled || options.length === 0) return;
    onUserTouch();
    if (!itemId) {
      onChange(options[0].id);
      return;
    }
    const idx = options.findIndex((o) => o.id === itemId);
    const next = (idx + dir + options.length) % options.length;
    onChange(options[next].id);
  };

  const surprise = () => {
    if (disabled || options.length === 0) return;
    onUserTouch();
    const pool = options.filter((o) => o.id !== itemId);
    const pick = (pool.length > 0 ? pool : options)[
      Math.floor(Math.random() * (pool.length > 0 ? pool.length : options.length))
    ];
    onChange(pick.id);
  };

  const requestUpload = () => {
    onUserTouch(); // Q3 future-proof
    onRequestUpload?.();
  };

  return (
    <div className="flex flex-col gap-2 py-3">
      <div className="flex items-center justify-between px-4">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
          {optional ? <span className="ml-1 opacity-60">(optional)</span> : null}
        </span>
        {!disabled && options.length > 1 ? (
          <button
            type="button"
            onClick={surprise}
            className="text-xs underline opacity-70 hover:opacity-100"
          >
            Surprise me
          </button>
        ) : null}
      </div>

      <div
        className="flex items-center gap-3 px-4"
        style={{ opacity: disabled ? 0.4 : 1 }}
      >
        <button
          type="button"
          aria-label={`Previous ${label}`}
          disabled={disabled || options.length < 2}
          onClick={() => cycle(-1)}
          className="w-8 h-8 rounded-full bg-muted disabled:opacity-30"
        >
          ‹
        </button>

        <div
          className="relative flex-1 aspect-square overflow-hidden"
          style={{ background: "var(--surface-2)", borderRadius: 20 }}
          onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
          onTouchEnd={(e) => {
            if (touchStartX === null || disabled) return;
            const dx = e.changedTouches[0].clientX - touchStartX;
            if (Math.abs(dx) >= 50) cycle(dx < 0 ? 1 : -1);
            setTouchStartX(null);
          }}
        >
          {disabled ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
              {disabledCopy ?? ""}
            </div>
          ) : current ? (
            <WardrobeImg
              item={current as never}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <button
              type="button"
              onClick={requestUpload}
              className="absolute inset-0 flex flex-col items-center justify-center text-sm text-muted-foreground"
            >
              <span className="text-2xl leading-none">+</span>
              <span className="mt-1">Add</span>
            </button>
          )}
        </div>

        <button
          type="button"
          aria-label={`Next ${label}`}
          disabled={disabled || options.length < 2}
          onClick={() => cycle(1)}
          className="w-8 h-8 rounded-full bg-muted disabled:opacity-30"
        >
          ›
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function StyleYourself({
  userId,
  wardrobeItems,
  occasion,
  weather,
  mood,
  gender,
  modelSetup,
  modelProfile,
  onClose,
  onOpenDrawer,
}: Props) {

  const [slots, setSlots] = useState<Record<SlotKey, string | null>>({
    top: null,
    bottom: null,
    shoes: null,
    outerwear: null,
    accessory: null,
  });
  const [currentOutfitId, setCurrentOutfitId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Optional slot visibility — collapsed by default per follow-up brief.
  // Auto-expanded when pre-population fills the slot from style-swipe-batch.
  const [optionalAdded, setOptionalAdded] = useState<{
    outerwear: boolean;
    accessory: boolean;
  }>({ outerwear: false, accessory: false });
  const userTouchedRef = useRef(false);

  const combosQ = useWardrobeCombinations({
    userId,
    occasion,
    weather,
    mood,
    enabled: true,
  });

  // Pre-population: primary style-swipe-batch → fallback to recency
  useEffect(() => {
    if (userTouchedRef.current) return;
    const first = combosQ.data?.[0];
    if (first) {
      const mapped = mapComboToSlots(first, wardrobeItems);
      setSlots(mapped);
      // Auto-expand optional rows when pre-population filled them.
      setOptionalAdded((prev) => ({
        outerwear: prev.outerwear || !!mapped.outerwear,
        accessory: prev.accessory || !!mapped.accessory,
      }));
      return;
    }
    if (combosQ.isSuccess && (!combosQ.data || combosQ.data.length === 0)) {
      setSlots(pickRecencyFallback(wardrobeItems));
    }
  }, [combosQ.data, combosQ.isSuccess, wardrobeItems]);

  // D4 — Dress auto-detect from Top slot's current category.
  const dressMode = useMemo(() => {
    const id = slots.top;
    if (!id) return false;
    const it = wardrobeItems.find((i) => i.id === id);
    return (it?.category || "").toLowerCase() === "dress";
  }, [slots.top, wardrobeItems]);

  const slotOptions = useMemo(
    () => ({
      top: itemsForSlot(wardrobeItems, "top"),
      bottom: itemsForSlot(wardrobeItems, "bottom"),
      shoes: itemsForSlot(wardrobeItems, "shoes"),
      outerwear: itemsForSlot(wardrobeItems, "outerwear"),
      accessory: itemsForSlot(wardrobeItems, "accessory"),
    }),
    [wardrobeItems],
  );

  const setSlot = (slot: SlotKey) => (id: string | null) =>
    setSlots((prev) => ({ ...prev, [slot]: id }));
  const touch = () => {
    userTouchedRef.current = true;
  };

  // ---- Try-it-on -----------------------------------------------------------

  async function handleTryItOn() {
    setError(null);
    const ids = [
      slots.top,
      dressMode ? null : slots.bottom,
      slots.shoes,
      slots.outerwear,
      slots.accessory,
    ].filter((x): x is string => !!x);

    if (ids.length < 1) {
      setError("Pick at least one item to try on");
      return;
    }

    setSubmitting(true);

    // Cache pre-flight (STYLE-YOURSELF-CACHE). Sorted picked-ids form the
    // cache key. RPC checks for a prior 'ready' style_yourself render by
    // this user with the same item set. Hit → skip LB4, copy image_url
    // into a new history row stamped with cached_from_outfit_id (D1b).
    const sortedIds = [...ids].sort();
    // PERSONALISATION-1: include personalisation_token (selfie_uploaded_at when
    // personalisation_enabled) so personalised users never hit a generic cache.
    let personalisationToken: string | null = null;
    try {
      const { data: prof } = await supabase
        .from("profiles")
        .select("personalisation_enabled, selfie_uploaded_at")
        .eq("id", userId)
        .maybeSingle();
      const p = prof as { personalisation_enabled?: boolean; selfie_uploaded_at?: string | null } | null;
      if (p?.personalisation_enabled && p.selfie_uploaded_at) {
        personalisationToken = p.selfie_uploaded_at;
      }
    } catch (e) {
      console.warn("[style-yourself] personalisation lookup failed:", e);
    }
    const { data: cached, error: cacheErr } = await supabase.rpc(
      "find_cached_style_yourself",
      {
        p_user_id: userId,
        p_sorted_item_ids: sortedIds,
        p_personalisation_token: personalisationToken ?? undefined,
      },
    );
    if (cacheErr) {
      console.warn("[style-yourself] cache lookup failed, falling through:", cacheErr);
    }
    if (cached && cached.length > 0) {
      const hit = cached[0] as { outfit_id: string; lookbook_image_url: string };
      const { data: cachedRow, error: cachedInsErr } = await supabase
        .from("outfits")
        .insert({
          user_id: userId,
          input_method: "wardrobe_combinator",
          analysis_mode: "style_yourself",
          analysis_status: "complete",
          lookbook_status: "ready",
          lookbook_image_url: hit.lookbook_image_url,
          cached_from_outfit_id: hit.outfit_id,
          occasion,
        } as never)
        .select("id")
        .single();

      if (cachedInsErr || !cachedRow) {
        // Fall back to displaying the original cached row directly.
        console.warn("[style-yourself] cache hit but insert failed; reusing original:", cachedInsErr);
        setCurrentOutfitId(hit.outfit_id);
      } else {
        setCurrentOutfitId((cachedRow as { id: string }).id);
      }
      setSubmitting(false);
      return;
    }

    const { data, error: insertErr } = await supabase
      .from("outfits")
      .insert({
        user_id: userId,
        input_method: "wardrobe_combinator",
        analysis_mode: "style_yourself",
        // Style_yourself rows have no analyse-outfit run — the user
        // picked items from their categorized wardrobe, so the row IS
        // already "analyzed" semantically. Setting analysis_status='complete'
        // on insert prevents useOutfitMetaPolling / useStuckDetection /
        // any future status-gated UI from hanging on these rows.
        // lookbook_status starts 'pending' and flips to 'ready' when
        // LB4 finishes — that's what the wardrobe surface polls.
        analysis_status: "complete",
        lookbook_status: "pending",
        occasion,
        // weather/mood deliberately omitted — columns don't exist on outfits.
        // They're still forwarded to LB4 via the function payload below.
      } as never)
      .select("id")
      .single();

    if (insertErr || !data) {
      setSubmitting(false);
      setError(insertErr?.message ?? "Could not start. Try again.");
      return;
    }

    const outfitId = (data as { id: string }).id;

    // Full LB4 contract — parity with LB1/2/3 + Wardrobe items-grid Generate.
    // gender stays top-level; model_setup carries appearance attributes.
    const invokePayload = {
      outfit_id: outfitId,
      user_id: userId,
      wardrobe_items: wardrobeItems.map((i) => ({
        id: i.id,
        name: i.name,
        category: i.category,
        color_hex: i.color_hex,
        image_url: i.image_url,
        image_path: i.image_path,
        cutout_path: i.cutout_path,
        cutout_status: i.cutout_status,
        pattern: i.pattern ?? null,
        style_tags: i.style_tags ?? null,
        vision_description: i.vision_description ?? null,
        lb3_description: i.lb3_description ?? null,
        lb4_description: i.lb4_description ?? null,
        lb4_eligible: i.lb4_eligible ?? null,
        lb3_eligible: i.lb3_eligible ?? null,
        last_used_at: i.last_used_at ?? null,
        times_used: i.times_used ?? null,
      })),
      must_include_item_ids: ids,
      occasion,
      weather,
      mood,
      gender,
      model_setup: modelSetup,
    };

    void supabase.functions
      .invoke("generate-lookbook4", { body: invokePayload })
      .then((res) => {
        if (res.error) {
          console.error("[style-yourself] LB4 invoke returned error:", res.error);
          void supabase
            .from("outfits")
            .update({
              lookbook_status: "failed",
              lookbook_error: `invoke_error: ${res.error.message ?? "unknown"}`,
            } as never)
            .eq("id", outfitId);
        }
      })
      .catch((err) => {
        console.error("[style-yourself] LB4 invoke threw:", err);
        void supabase
          .from("outfits")
          .update({
            lookbook_status: "failed",
            lookbook_error: `invoke_threw: ${(err as { message?: string })?.message ?? "unknown"}`,
          } as never)
          .eq("id", outfitId);
      });

    setCurrentOutfitId(outfitId);
    setSubmitting(false);
  }

  // ---- Polling (component-local terminal derivation, Q1) -------------------

  const meta = useOutfitMetaPolling(currentOutfitId, false, false);
  const lb4Ready =
    meta?.lookbook_status === "ready" && !!meta?.lookbook_image_url;
  const lb4Failed = meta?.lookbook_status === "failed";

  // ---- View ----------------------------------------------------------------

  // Unified result surface — handles loading skeleton AND ready states.
  // Replaces the separate "Putting it together" screen (Brief STYLE-YOURSELF-RESULT-PARITY).
  if (currentOutfitId && !lb4Failed) {
    return (
      // Bounded height so StyleYourselfResult's `h-full` chain resolves and
      // its sticky footer pins. Matches the wrapper Wardrobe.tsx used to
      // apply unconditionally — now scoped to the result branch only, so
      // the picker can render in natural flow (FAB is position:fixed).
      // Constants mirror Wardrobe.tsx root paddings (safe-area + 12 + 78).
      <div
        className="flex flex-col"
        style={{
          height:
            "calc(100dvh - var(--safe-top, 0px) - var(--safe-bottom, 0px) - 12px - 78px)",
        }}
      >
        <StyleYourselfResult
          outfitId={currentOutfitId}
          imageUrl={lb4Ready ? meta!.lookbook_image_url! : null}
          loading={!lb4Ready}
          modelProfile={modelProfile}
          onBack={() => setCurrentOutfitId(null)}
          onTryAnother={() => {
            // Clear slots + allow fresh pre-population next mount.
            userTouchedRef.current = false;
            setSlots({
              top: null,
              bottom: null,
              shoes: null,
              outerwear: null,
              accessory: null,
            });
            setCurrentOutfitId(null);
          }}
          onTweakCombo={() => {
            // Preserve slots — return to picker as-is.
            setCurrentOutfitId(null);
          }}
        />
      </div>
    );
  }


  if (currentOutfitId && lb4Failed) {
    return (
      <div className="flex flex-col h-full" style={{ background: "#FFFCF8" }}>
        <StyleYourselfHeader
          eyebrow="LIKE2FIT · STYLE YOURSELF"
          title="Your look"
          onBack={() => setCurrentOutfitId(null)}
        />
        <div className="flex flex-col items-center gap-4 p-8">
          <p className="text-base">That didn't work. Try a different combo.</p>
          <Button onClick={() => setCurrentOutfitId(null)}>Back</Button>
        </div>
      </div>
    );
  }

  // Picker view
  const canSubmit =
    !!slots.top || !!slots.bottom || !!slots.shoes || !!slots.outerwear || !!slots.accessory;

  return (
    <div className="flex flex-col" style={{ background: "#FFFCF8" }}>

      <StyleYourselfHeader
        eyebrow="LIKE2FIT · STYLE YOURSELF"
        title="Style yourself"
        onBack={onClose}
        rightAction={
          <button
            type="button"
            onClick={onOpenDrawer}
            aria-label="Open menu"
            style={{
              width: 36,
              height: 36,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#0A0A0A",
            }}
          >
            <i className="ti ti-menu-2" style={{ fontSize: 20 }} aria-hidden="true" />
          </button>
        }
      />


      <div>

        <SlotRow
          slot="top"
          label="Top"
          itemId={slots.top}
          options={slotOptions.top}
          optional={false}
          onChange={setSlot("top")}
          onUserTouch={touch}
        />
        <SlotRow
          slot="bottom"
          label="Bottom"
          itemId={slots.bottom}
          options={slotOptions.bottom}
          optional={false}
          disabled={dressMode}
          disabledCopy="Covered by dress"
          onChange={setSlot("bottom")}
          onUserTouch={touch}
        />
        <SlotRow
          slot="shoes"
          label="Shoes"
          itemId={slots.shoes}
          options={slotOptions.shoes}
          optional={false}
          onChange={setSlot("shoes")}
          onUserTouch={touch}
        />
        {optionalAdded.outerwear ? (
          <SlotRow
            slot="outerwear"
            label="Outerwear"
            itemId={slots.outerwear}
            options={slotOptions.outerwear}
            optional={true}
            onChange={setSlot("outerwear")}
            onUserTouch={touch}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              touch();
              setOptionalAdded((p) => ({ ...p, outerwear: true }));
            }}
            className="mx-4 my-2 py-2 text-sm text-muted-foreground border border-dashed rounded-lg hover:opacity-80"
          >
            + Outerwear
          </button>
        )}
        {optionalAdded.accessory ? (
          <SlotRow
            slot="accessory"
            label="Accessory"
            itemId={slots.accessory}
            options={slotOptions.accessory}
            optional={true}
            onChange={setSlot("accessory")}
            onUserTouch={touch}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              touch();
              setOptionalAdded((p) => ({ ...p, accessory: true }));
            }}
            className="mx-4 my-2 py-2 text-sm text-muted-foreground border border-dashed rounded-lg hover:opacity-80"
          >
            + Accessory
          </button>
        )}
      </div>

      {error ? (
        <p className="px-4 py-2 text-sm text-destructive">{error}</p>
      ) : null}

      <button
        type="button"
        onClick={handleTryItOn}
        disabled={!canSubmit || submitting}
        aria-label={submitting ? "Starting" : "Try it on"}
        className="rounded-full inline-flex items-center justify-center disabled:opacity-40"
        style={{
          position: "fixed",
          right: 16,
          bottom: "calc(var(--safe-bottom, 0px) + 90px)",
          width: 56,
          height: 56,
          zIndex: 50,
          background: "var(--primary)",
          color: "var(--primary-foreground)",
          border: "none",
          boxShadow: (!canSubmit || submitting)
            ? "none"
            : "0 6px 16px rgba(200, 75, 47, 0.30)",
          cursor: (!canSubmit || submitting) ? "default" : "pointer",
        }}
      >
        {!submitting && (
          <svg
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              overflow: "visible",
              pointerEvents: "none",
            }}
            viewBox="0 0 56 56"
            aria-hidden="true"
          >
            <defs>
              <path id="fab-try-arc" d="M -8,28 A 36,36 0 0 1 64,28" fill="none" />
            </defs>
            <text
              fill="var(--primary)"
              fontSize="9"
              fontWeight={500}
              letterSpacing="9"
              textAnchor="middle"
            >
              <textPath href="#fab-try-arc" startOffset="50%">TRY</textPath>
            </text>
          </svg>
        )}
        <i
          className={submitting ? "ti ti-loader-2 animate-spin" : "ti ti-sparkles"}
          style={{ fontSize: 24 }}
          aria-hidden="true"
        />

      </button>
    </div>
  );
}
